import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';

const QuickDecisionModal = ({ show, onClose, onSubmit, interview, newStatus, loading }) => {
  const [formData, setFormData] = useState({
    notes: '',
    offerAmount: '',
    rejectionReason: ''
  });

  const getModalConfig = (status) => {
    switch (status) {
      case 'under_consideration':
        return {
          title: 'Move to Consideration',
          icon: 'Clock',
          color: 'amber',
          description: 'Add notes about why this candidate is being considered for hire.',
          showOffer: false,
          showRejection: false
        };
      case 'hired':
        return {
          title: 'Mark as Hired',
          icon: 'UserCheck',
          color: 'green', 
          description: 'Congratulations! Record the hiring details.',
          showOffer: true,
          showRejection: false
        };
      case 'not_moving_forward':
        return {
          title: 'Not Moving Forward',
          icon: 'X',
          color: 'red',
          description: 'Provide feedback for the candidate (optional).',
          showOffer: false,
          showRejection: true
        };
      default:
        return null;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      interview_id: interview.id,
      status: newStatus,
      notes: formData.notes,
      offer_amount: formData.offerAmount ? parseFloat(formData.offerAmount) : null,
      rejection_reason: formData.rejectionReason
    });
  };

  if (!show || !newStatus) return null;

  const config = getModalConfig(newStatus);
  if (!config) return null;

  const colorClasses = {
    amber: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClasses[config.color]}`}>
              <Icon name={config.icon} size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{config.title}</h3>
              <p className="text-sm text-gray-600">{interview?.candidate_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600">{config.description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {config.showOffer && (
            <Input
              label="Hourly Rate Offered ($)"
              type="number"
              step="0.01"
              placeholder="25.00"
              value={formData.offerAmount}
              onChange={(e) => setFormData(prev => ({ ...prev, offerAmount: e.target.value }))}
            />
          )}

          {config.showRejection && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <select
                value={formData.rejectionReason}
                onChange={(e) => setFormData(prev => ({ ...prev, rejectionReason: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select reason (optional)</option>
                <option value="skills_not_match">Skills don't match requirements</option>
                <option value="experience_insufficient">Insufficient experience</option>
                <option value="communication_concerns">Communication concerns</option>
                <option value="availability_mismatch">Availability doesn't match needs</option>
                <option value="budget_constraints">Budget constraints</option>
                <option value="found_better_candidate">Found a better fit</option>
                <option value="other">Other</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes {config.showRejection ? '(Optional)' : ''}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder={
                newStatus === 'hired' 
                  ? 'Start date, special agreements, next steps...'
                  : newStatus === 'under_consideration'
                  ? 'What makes this candidate promising?'
                  : 'Additional feedback for the candidate...'
              }
            />
          </div>

          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              iconName={config.icon}
              loading={loading}
            >
              {loading ? 'Updating...' : config.title}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickDecisionModal;
